/**
 * Out-of-SSA translation. Replaces each phi with explicit copy assignments:
 * for every (predecessor, value) input of `phiResult = phi(...)`, a
 * `phiResult = value` assignment is appended to that predecessor before its
 * terminator. Copies between versions of the same local collapse to
 * self-assignments and are removed during lowering; copies that materialize a
 * stack-merge value (e.g. an `if (result T)` whose branches push values) are
 * what keep that value from being dropped.
 *
 * Must run after the SSA optimization passes (which assume single-definition
 * form) and before structural analysis (which copies block instruction lists).
 */

import { SsaBlock, SsaFunction, SsaInstr } from './SsaTypes';

const TERMINATOR_KINDS = new Set(['branch', 'branch_if', 'branch_table', 'return', 'unreachable']);

function terminatorIndex(block: SsaBlock): number {
  for (let index = block.instructions.length - 1; index >= 0; index--) {
    if (TERMINATOR_KINDS.has(block.instructions[index].kind)) {
      return index;
    }
  }
  return -1;
}

function insertBeforeTerminator(block: SsaBlock, instruction: SsaInstr): void {
  const index = terminatorIndex(block);
  if (index < 0) {
    block.instructions.push(instruction);
  } else {
    block.instructions.splice(index, 0, instruction);
  }
}

interface PendingCopy {
  predecessor: SsaBlock;
  copy: SsaInstr;
}

function collectPhiCopies(ssaFunc: SsaFunction, blockById: Map<number, SsaBlock>): PendingCopy[] {
  const pending: PendingCopy[] = [];
  for (const block of ssaFunc.blocks) {
    for (const instruction of block.instructions) {
      if (instruction.kind !== 'phi') {
        continue;
      }
      for (const input of instruction.inputs) {
        const predecessor = blockById.get(input.blockId);
        if (predecessor) {
          pending.push({ predecessor, copy: { kind: 'assign', result: instruction.result, value: input.value } });
        }
      }
    }
  }
  return pending;
}

export function eliminatePhis(ssaFunc: SsaFunction): void {
  const blockById = new Map<number, SsaBlock>();
  for (const block of ssaFunc.blocks) {
    blockById.set(block.id, block);
  }

  // Collect every copy before mutating any instruction list so a self-loop
  // block (its own predecessor) is read before it is modified.
  const pendingCopies = collectPhiCopies(ssaFunc, blockById);
  for (const { predecessor, copy } of pendingCopies) {
    insertBeforeTerminator(predecessor, copy);
  }
  for (const block of ssaFunc.blocks) {
    block.instructions = block.instructions.filter(instruction => instruction.kind !== 'phi');
  }
}
